import { useState, useRef } from "react";
import { Mail, Send } from "lucide-react";
import { Turnstile } from "@marsidev/react-turnstile";
import { useLanguage } from "@/hooks/use-language";
import { TURNSTILE_SITE_KEY } from "@/constants/config";
import { SuccessPopup } from "./success-popup";

// Contact Section
export const ContactSection = () => {
  const { t } = useLanguage();
  const turnstileRef = useRef(null);
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    if (!email.trim() || !subject.trim() || !content.trim()) {
      setError("Invalid content");
      return;
    }

    if (subject.length < 5) {
      setError("Subject must be at least 5 characters");
      return;
    }

    if (content.length < 10) {
      setError("Message must be at least 10 characters");
      return;
    }

    const captchaToken = await turnstileRef.current?.getResponsePromise();
    console.log("turnstileRef ==> ", turnstileRef);
    console.log("turnstileRef.current ==> ", turnstileRef.current);
    console.log("captchaToken ==> ", captchaToken);
    if (!captchaToken) {
      setError("Cannot verify you are human");
      return;
    }

    try {
      setSending(true);
      setError("");
      const response = await fetch(
        "https://golden-horseshoe.phuongai.com/feedback/public",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            captcha_token: captchaToken,
            email,
            subject,
            content,
          }),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to send message");
      }

      // Success - Reset everything
      setSent(true);
      setShowSuccessPopup(true);
      setEmail("");
      setSubject("");
      setContent("");

      if (turnstileRef.current) {
        turnstileRef.current.reset();
      }

      setTimeout(() => setSent(false), 3000);
    } catch (err) {
      setError(t("contact.error"));

      if (turnstileRef.current) {
        turnstileRef.current.reset();
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <SuccessPopup
        show={showSuccessPopup}
        message={t("contact.sent") + " " + t("contact.description")}
        onClose={() => setShowSuccessPopup(false)}
      />

      <section
        id="contact"
        className="relative py-24 lg:py-32 bg-[#030014]"
        data-testid="contact-section"
      >
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-4xl h-[1px] bg-gradient-to-r from-transparent via-[#FF2E63]/30 to-transparent" />

        <div className="max-w-2xl mx-auto px-6 sm:px-10 lg:px-16">
          <div className="text-center mb-12">
            <p className="text-[#FF2E63] text-xs tracking-[0.4em] uppercase mb-4">
              {t("contact.subtitle")}
            </p>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-white mb-4">
              {t("contact.title1")}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF2E63] to-pink-400">
                {t("contact.title2")}
              </span>
            </h2>
            <p className="text-white/50">{t("contact.description")}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label
                htmlFor="email"
                className="block text-white/70 text-sm mb-2"
              >
                {t("contact.email")}
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("contact.emailPlaceholder")}
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-[#FF2E63]/50 transition-colors"
                data-testid="contact-email"
                max={255}
                required
              />
            </div>

            <div>
              <label
                htmlFor="subject"
                className="block text-white/70 text-sm mb-2"
              >
                {t("contact.subject")}
              </label>
              <input
                id="subject"
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value.slice(0, 500))}
                placeholder={t("contact.subjectPlaceholder")}
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-[#FF2E63]/50 transition-colors"
                data-testid="contact-subject"
                max={500}
                required
              />
              <p className="text-white/30 text-xs mt-1 text-right">
                {subject.length}/500
              </p>
            </div>

            <div>
              <label
                htmlFor="content"
                className="block text-white/70 text-sm mb-2"
              >
                {t("contact.message")}
              </label>
              <textarea
                id="content"
                value={content}
                onChange={(e) => setContent(e.target.value.slice(0, 2000))}
                placeholder={t("contact.messagePlaceholder")}
                rows={6}
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-[#FF2E63]/50 transition-colors resize-none"
                data-testid="contact-content"
                required
                maxLength={2000}
              />
              <p className="text-white/30 text-xs mt-1 text-right">
                {content.length}/2000
              </p>
            </div>

            <div className="flex justify-center">
              <Turnstile
                ref={turnstileRef}
                siteKey={TURNSTILE_SITE_KEY}
                onError={(error) => {
                  console.error("Captcha verification failed: " + error);
                }}
              />
            </div>

            <button
              type="submit"
              disabled={
                sending ||
                !email?.trim()?.length ||
                !subject?.trim()?.length ||
                !content?.trim()?.length
              }
              className="w-full inline-flex items-center justify-center gap-3 bg-[#FF2E63] text-white px-8 py-4 rounded-full font-semibold transition-all duration-300 hover:shadow-[0_0_40px_rgba(255,46,99,0.5)] disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="contact-send-btn"
            >
              {sending ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {t("contact.sending")}
                </>
              ) : sent ? (
                <>
                  <Mail className="w-5 h-5" />
                  {t("contact.sent")}
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  {t("contact.send")}
                </>
              )}
            </button>

            {error && (
              <p className="text-red-400 text-sm text-center">{error}</p>
            )}
          </form>
        </div>
      </section>
    </>
  );
};
